import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { getCompanyDetails } from 'services/api/company-details'
import { updateJobAds } from 'services/api/modules/recruitment/job-advertisment'
import default_company_photo from 'assets/images/default_company_photo.png'
import useOpenClose from 'services/hooks/useOpenClose'
import ActivateJobAdsModal from './ActivateJobAdsModal'
import SnackbarRecruitment from 'module/recruitment/reusable/SnackbarRecruitment'
import AdxInfoModal from 'components/reusable/modal/AdxInfoModal'
import ViewJobLink from './ViewJobLink'
import { renderSkills } from 'components/custom/AdxLabelRenderer'
import AdxButton from 'components/custom/AdxButton'
const { Box, Typography, Divider, CardMedia } = require('@mui/material')
const { default: AdxDrawerComponent } = require('components/reusable/drawer/AdxDrawerComponent')

const ADNEXIO_JOBS_URL = process.env.REACT_APP_ADNEXIO_JOBS_URL

const ViewJobAdsDrawer = ({ drawerState, handleDrawer, id, handleChangeStatus, data, refetch }) => {
  const { data: companyDetails } = useQuery('companyDetails', getCompanyDetails)

  // modal/snackbar
  const activateModal = useOpenClose(false)
  const deactivateModal = useOpenClose(false)
  const successActivateSnackbar = useOpenClose(false)
  const successDeactivateSnackbar = useOpenClose(false)
  const errorSnackbar = useOpenClose(false)

  // Call API
  const queryClient = useQueryClient()
  const {
    isLoading: activateIsLoading,
    isSuccess: activateIsSuccess,
    error: activateError,
    data: activateData,
    isError: activateIsError,
    mutate: activateMutate,
  } = useMutation((data) => updateJobAds(id, 'activate', data), {
    onSuccess: async (response) => {
      successActivateSnackbar.handleOpen()
      activateModal.handleClose()
      refetch()
    },
    onError: async (error) => {
      errorSnackbar.handleOpen()
    },
  })

  const {
    isLoading: deactivateIsLoading,
    isSuccess: deactivateIsSuccess,
    error: deactivateError,
    isError: deactivateIsError,
    data: deactivateData,
    mutate: deactivateMutate,
  } = useMutation((id) => updateJobAds(id, 'deactivate'), {
    onSuccess: async (response) => {
      successDeactivateSnackbar.handleOpen()
      refetch()
    },
    onError: async (error) => {
      errorSnackbar.handleOpen()
    },
  })

  useEffect(() => {
    if (deactivateIsSuccess) {
      deactivateModal.handleClose()
    }
  }, [deactivateIsSuccess])

  //handle working experience
  let experienceString
  if (data?.year_of_experience === 1) {
    experienceString = 'Fresh graduates are welcome to apply'
  } else if (data?.year_of_experience === 2) {
    experienceString = '1-3 years'
  } else if (data?.year_of_experience === 3) {
    experienceString = '3-5 years'
  } else if (data?.year_of_experience === 4) {
    experienceString = 'more than 5 years'
  } else {
    experienceString = '-'
  }

  const jobBoardUrl = `${window.location.origin}/job-board/${data?.company_name}/${data?.tenant_identifier}/${data?.id}`
  const adnexioJobsUrl = ADNEXIO_JOBS_URL

  const DeactivateJobAdsModal = () => (
    <AdxInfoModal
      variant={'error'}
      modalWidth={600}
      muiIcon="error_icon"
      open={deactivateModal.isOpen}
      onClose={deactivateModal.handleClose}
      iconColor="error.main"
      title="Deactivate Job Advertisement?"
      subtitle="Are you sure you want to deactivate the job advertisement? This job advertisement will not be visible to the jobseeker and you will not receive new applications"
      footerButtons
      firstBtnLabel={'Deactivate'}
      firstBtnHandle={() => {
        deactivateMutate(id)
        deactivateModal.handleClose = true
      }}
      secondBtnLabel={'Cancel'}
      secondBtnHandle={deactivateModal.handleClose}
    />
  )

  const newDate = new Date(data?.expired_date)
  const formattedDate = newDate.toLocaleDateString('en-GB', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  })

  const arraySkill = data?.skill?.split(',') ?? null

  const Skills = ({ word }) => {
    return (
      <Box
        display="flex"
        alignItems="center"
        bgcolor="gray-background.main"
        sx={{
          borderRadius: '6px',
        }}
      >
        <Typography variant="body1" color="dark-text.main" padding={1} align="center">
          {word}
        </Typography>
      </Box>
    )
  }
  return (
    <AdxDrawerComponent
      state={drawerState}
      anchor="right"
      onClose={handleDrawer}
      noPaddingContent
      headerprops={
        <Box sx={{ display: 'flex', justifyContent: 'start', gap: 2 }}>
          <CardMedia
            component="img"
            sx={{
              height: 52,
              width: 52,
              objectFit: 'cover',
              borderRadius: '50%',
            }}
            alt="Company Image"
            src={companyDetails?.data?.organization_logo || default_company_photo}
            loading="lazy"
          />
          <Box>
            <Typography variant="subtitle1">{data?.position_title}</Typography>
            <Typography variant="subtitle1" color="light-gray-text.main">
              {companyDetails?.data?.organization_name || 'No company name'}
            </Typography>
            {data?.advertisement_status === 1 ? (
              <Typography variant="subtitle1" color="light-gray-text.main">
                Expires on {formattedDate}
              </Typography>
            ) : null}
          </Box>
        </Box>
      }
      footerprops={
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            width: '100%',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box>
            <Typography textAlign="left " variant="subtitle1" color="dark-text.main" align="center">
              {data?.advertisement_status === 1
                ? 'Deactivate advertisement?'
                : 'Activate advertisement?'}
            </Typography>
            <Typography
              textAlign="left "
              variant="body2"
              color="light-gray-text.main"
              align="center"
            >
              {data?.advertisement_status === 1
                ? 'Jobseekers will not be able to see it'
                : 'This will make it visible to jobseekers'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <AdxButton variant={'outlined'} onClick={handleDrawer('right', false)}>
              Cancel
            </AdxButton>
            {data?.advertisement_status === 1 ? (
              <AdxButton
                variant={'contained-destructive'}
                sx={{ width: 150 }}
                onClick={() => {
                  deactivateModal.handleOpen()
                }}
              >
                Deactivate
              </AdxButton>
            ) : (
              <AdxButton
                variant={'contained'}
                sx={{ width: 150 }}
                onClick={() => {
                  activateModal.handleOpen()
                }}
              >
                Activate
              </AdxButton>
            )}
          </Box>
        </Box>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', p: 3 }}>
        <Box display="flex" flexDirection="column" gap={4}>
          {data?.advertisement_status === 1 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: 1,
              }}
            >
              <Typography textAlign="left" variant="h4" color="dark-text.main" fontWeight="bold">
                Published on
              </Typography>
              <ViewJobLink adnexioJobsUrl={adnexioJobsUrl} jobBoardUrl={jobBoardUrl} />
            </Box>
          )}
          {/* job detail */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography textAlign="left" variant="h4" color="dark-text.main" fontWeight="bold">
              Job Details
            </Typography>
            <Box display="flex" columnGap={4}>
              <Typography
                textAlign="left "
                variant="body1"
                color="light-gray-text.main"
                align="center"
                width={130}
              >
                Position level
              </Typography>
              <Typography
                textAlign="left "
                variant="body1"
                color="dark-text.main"
                align="center"
                textTransform="capitalize"
              >
                {data?.job_level_name}
              </Typography>
            </Box>
            <Box display="flex" columnGap={4}>
              <Typography
                textAlign="left "
                variant="body1"
                color="light-gray-text.main"
                align="center"
                width={130}
              >
                Specialization
              </Typography>
              <Typography
                textAlign="left "
                variant="body1"
                color="dark-text.main"
                align="center"
                textTransform="capitalize"
              >
                {data?.job_background_name}
              </Typography>
            </Box>
            <Box display="flex" columnGap={4}>
              <Typography
                textAlign="left "
                variant="body1"
                color="light-gray-text.main"
                align="center"
                width={130}
              >
                Employment type
              </Typography>
              <Typography
                textAlign="left "
                variant="body1"
                color="dark-text.main"
                align="center"
                textTransform="capitalize"
              >
                {data?.position_type}
              </Typography>
            </Box>
            <Box display="flex" columnGap={4}>
              <Typography
                textAlign="left "
                variant="body1"
                color="light-gray-text.main"
                align="center"
                width={130}
              >
                Salary
              </Typography>
              <Typography
                textAlign="left "
                variant="body1"
                color="dark-text.main"
                align="center"
                textTransform="capitalize"
              >
                RM {data?.min_salary} - RM {data?.max_salary}
              </Typography>
            </Box>
            <Box display="flex" columnGap={4}>
              <Typography
                textAlign="left "
                variant="body1"
                color="light-gray-text.main"
                align="center"
                width={130}
              >
                Work location
              </Typography>
              <Typography
                textAlign="left "
                variant="body1"
                color="dark-text.main"
                align="center"
                textTransform="capitalize"
              >
                {data?.city}, {data?.state}
              </Typography>
            </Box>
          </Box>
          {/* job description */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography textAlign="left" variant="h4" color="dark-text.main" fontWeight="bold">
              Job Description
            </Typography>
            <Typography
              whiteSpace="pre-wrap"
              textAlign="left "
              variant="body1"
              color="dark-text.main"
              align="center"
            >
              {data?.job_description}
            </Typography>
          </Box>
          {/* job requirement */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography textAlign="left" variant="h4" color="dark-text.main" fontWeight="bold">
              Job Requirements
            </Typography>
            <Box sx={{ display: 'flex', gap: 4 }}>
              <Typography
                textAlign="left "
                variant="body1"
                color="light-gray-text.main"
                align="center"
                width={130}
              >
                Qualification
              </Typography>
              <Typography
                textAlign="left "
                variant="body1"
                color="dark-text.main"
                align="center"
                textTransform="capitalize"
              >
                {data?.qualification}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 4 }}>
              <Typography
                textAlign="left "
                variant="body1"
                color="light-gray-text.main"
                align="center"
                width={130}
              >
                Work Experience
              </Typography>
              <Typography
                textAlign="left "
                variant="body1"
                color="dark-text.main"
                align="center"
                textTransform="capitalize"
              >
                {experienceString}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography
                textAlign="left "
                variant="body1"
                color="light-gray-text.main"
                align="center"
                width={130}
              >
                Skills
              </Typography>
              <Box display="flex" flexWrap="wrap" alignItems="center" sx={{ gap: 1 }}>
                {arraySkill?.map((item) => renderSkills(item))}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography
                textAlign="left "
                variant="body1"
                color="light-gray-text.main"
                align="center"
                width={170}
              >
                Other Requirements
              </Typography>
              <Typography
                whiteSpace="pre-wrap"
                textAlign="left "
                variant="body1"
                color="dark-text.main"
                align="center"
              >
                {data?.job_requirement}
              </Typography>
            </Box>
          </Box>
        </Box>

        {activateModal.isOpen ? (
          <ActivateJobAdsModal
            open={activateModal.isOpen}
            onClose={activateModal.handleClose}
            handleClose={activateModal.handleClose}
            mutate={activateMutate}
            isLoading={activateIsLoading}
          />
        ) : null}

        {deactivateModal.isOpen ? <DeactivateJobAdsModal /> : null}

        {successActivateSnackbar.isOpen ? (
          <SnackbarRecruitment
            autoHideDuration={4000}
            status="success"
            TransitionComponent="slide"
            message="Job ad activated"
            open={successActivateSnackbar.isOpen}
            handleClose={successActivateSnackbar.handleClose}
          />
        ) : null}

        {successDeactivateSnackbar.isOpen ? (
          <SnackbarRecruitment
            status="success"
            autoHideDuration={4000}
            TransitionComponent="slide"
            message="Job ad deactivated"
            open={successDeactivateSnackbar.isOpen}
            handleClose={successDeactivateSnackbar.handleClose}
          />
        ) : null}

        {errorSnackbar.isOpen ? (
          <SnackbarRecruitment
            status="error"
            autoHideDuration={4000}
            TransitionComponent="slide"
            message="An error has occured. Please try again"
            open={errorSnackbar.isOpen}
            handleClose={errorSnackbar.handleClose}
          />
        ) : null}
      </Box>
    </AdxDrawerComponent>
  )
}
export default ViewJobAdsDrawer
